/**
 * Simulates a typing animation for React state
 * @param setText Function to update the state
 * @param text Final text to type
 * @param typingSpeed Speed of typing in milliseconds
 * @param callback Optional callback function to run after typing completes
 */
export const simulateTyping = (
  setText: (text: string) => void,
  text: string,
  typingSpeed: number = 100,
  callback?: () => void
): void => {
  // Set initial value to empty
  setText('');
  
  let typedText = '';
  let i = 0;
  
  const typeNextCharacter = () => {
    if (i < text.length) {
      // Add the next character
      typedText += text.charAt(i);
      
      // Update React state directly
      setText(typedText);
      
      // Move to next character
      i++;
      setTimeout(typeNextCharacter, typingSpeed);
    } else {
      // Typing complete, run callback if provided
      if (callback) {
        setTimeout(callback, typingSpeed * 2);
      }
    }
  };
  
  // Start typing animation
  setTimeout(typeNextCharacter, typingSpeed);
};

/**
 * Simulates pressing the Enter key on an input element
 * @param inputRef Reference to the input element
 */
export const simulateEnterPress = (
  inputRef: React.RefObject<HTMLInputElement>
): void => {
  if (!inputRef.current) return;
  
  // Get the input element
  const input = inputRef.current;
  
  // Create and dispatch a keypress event for Enter key
  const event = new KeyboardEvent('keypress', {
    key: 'Enter',
    code: 'Enter',
    keyCode: 13,
    which: 13,
    bubbles: true
  });
  
  input.dispatchEvent(event);
};
